import {
  Component,
  ChangeDetectionStrategy,
  computed,
  signal,
  input,
  effect,
  HostBinding,
} from '@angular/core';
import { ImageUrlService } from '../../services/images/image-url.service';

type Preset = 'avatar' | 'card' | 'hero' | 'custom';

@Component({
  selector: 'app-image',
  standalone: true,
  imports: [],
  templateUrl: './image.html',
  styleUrl: './image.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppImageComponent {
  // === Inputs ===============================================================
  src = input.required<string>();
  alt = input<string>('');
  sizes = input<string>('(max-width: 768px) 90vw, (max-width: 1200px) 60vw, 1200px');
  aspectRatio = input<string | undefined>();
  preset = input<Preset>('card');
  priority = input<boolean>(false);
  placeholderSrc = input<string | undefined>();
  ariaHidden = input<boolean>(false);
  width = input<number | undefined>();
  height = input<number | undefined>();
  imgClass = input<string>('');
  fill = input<boolean>(false);
  fit = input<'cover' | 'contain'>('cover');
  intrinsic = input<boolean>(false);

  @HostBinding('class.app-image-host--fill')
  get hostFill(): boolean {
    return this.fill();
  }

  // === State ================================================================
  /** True when the final <img> has completed loading (success OR error if you want to stop skeleton). */
  loaded = signal(false);

  /** Set to true when the <img> errors; used to fall back to original src. */
  private hadError = signal(false);

  constructor(private readonly img: ImageUrlService) {
    // Reset UI state whenever the source changes
    effect(() => {
      void this.src();           // track source change
      this.hadError.set(false);
      this.loaded.set(false);    // show skeleton again for the new image
    });
  }

  // === Srcsets ==============================================================
  private readonly avifPairs: Record<Preset, Array<[string, number]>> = {
    avatar: [['w128_avif', 128], ['w256_avif', 256]],
    card:   [['w256_avif', 256], ['w640_avif', 640], ['w1024_avif', 1024], ['w1600_avif', 1600]],
    hero:   [['w1024_avif', 1024], ['w1600_avif', 1600]],
    custom: [],
  };

  private readonly webpPairs: Record<Preset, Array<[string, number]>> = {
    avatar: [['w128_webp', 128], ['w256_webp', 256]],
    card:   [['w256_webp', 256], ['w640_webp', 640], ['w1024_webp', 1024], ['w1600_webp', 1600]],
    hero:   [['w1024_webp', 1024], ['w1600_webp', 1600], ['w2560_webp', 2560]],
    custom: [],
  };

  avifSrcset = computed(() => {
    const p = this.preset();
    if (p === 'custom') return '';
    return this.img.srcset(this.src(), this.avifPairs[p]);
  });

  webpSrcset = computed(() => {
    const p = this.preset();
    if (p === 'custom') return '';
    return this.img.srcset(this.src(), this.webpPairs[p]);
  });

  fallbackSrc = computed(() => {
    if (this.hadError()) return this.src();
    const p = this.preset();
    const mid = p === 'avatar' ? 'w128_webp' : p === 'hero' ? 'w1600_webp' : 'w1024_webp';
    return this.img.derivative(mid, this.src());
  });

  // === Handlers =============================================================
  onLoad = (): void => {
    // Final image painted → hide skeleton & placeholder
    this.loaded.set(true);
  };

  onError = (): void => {
    if (!this.hadError()) this.hadError.set(true);
    // Stop the skeleton on error to avoid infinite shimmer (optional UX choice)
    this.loaded.set(true);
  };
}
